import * as dayjs from "dayjs";
import { DATE_FORMAT_DD_MM_YYYY_HH_mm, DATE_FORMAT_HH_mm } from "../constants/DateConstant";

/**
 * Shared read helpers for SooCool data.
 *
 * Two shapes of the very same shipment reach this app:
 *
 * 1. the write-back we store on our own order (`orders/{docId}.soocool`, see
 *    planning/soocool-koppeling.md "Write-back op de order"). It is flattened: the colli weight is
 *    stored as `weightGram` and the planned time window sits directly on the task.
 * 2. the raw SooCool Order object handed back by the read-only proxy
 *    (`GET /api/shipping/soocool/search`), where the colli weight is `weight` and the planned time
 *    window lives in `trackAndTraceInfo`.
 *
 * Every helper below reads both shapes, so the order details drawer and the SooCool page render
 * the same shipment identically instead of each growing its own field lookups.
 */

export const SOOCOOL_TASK_TYPE_DELIVERY = "delivery";
export const SOOCOOL_TASK_TYPE_PICKUP = "pickup";

/** Every `taskState` the API can return (planning/soocool-koppeling.md, "Response"). */
export const SOOCOOL_TASK_STATES = ["processing", "accepted", "planned", "delivered", "cancelled", "not_delivered"];

const SOOCOOL_TASK_STATE_COLORS = {
  processing: "default",
  accepted: "blue",
  planned: "cyan",
  delivered: "green",
  cancelled: "red",
  not_delivered: "volcano",
};

const SOOCOOL_TRANSPORT_REQUIREMENT_COLORS = {
  cooled: "blue",
  frozen: "cyan",
  age_check: "gold",
};

export const getSooCoolTaskStateColor = (taskState) => SOOCOOL_TASK_STATE_COLORS[taskState] || "default";

export const getSooCoolTransportRequirementColor = (requirement) =>
  SOOCOOL_TRANSPORT_REQUIREMENT_COLORS[requirement] || "default";

/**
 * The task the staff actually care about: the *current* delivery leg.
 *
 * We book exactly one `delivery` task per order, so a second one can only have been added by
 * SooCool itself - in practice a redelivery after a failed stop. The first attempt stays in the
 * list forever, carrying its `not_delivered` state and its `completionProblem`, so reading the
 * first delivery task would show staff the attempt that failed instead of the one that happened.
 *
 * **Must stay in step with the api.** `SooCoolShipmentService.isDelivered` (nl.gideloff.webshopapi.service)
 * derives the order status - COMPLETED vs SHIPPED - from the delivery task with the **highest
 * `taskId`**, falling back to list position only when no delivery task carries an id. This helper
 * uses the same rule, including the same `>` tie-break: pick a different task here and the drawer
 * contradicts the status next to it.
 *
 * Since 2026-10-08 every order also carries a `pickup` task: the boxes collected at the warehouse the
 * working day before. It never counts here. SooCool accepts no order without a delivery task, so the
 * fallback to the first task only keeps malformed data readable.
 */
export const getSooCoolDeliveryTask = (soocoolOrder) => {
  const tasks = soocoolOrder?.tasks || [];
  const deliveryTasks = tasks.filter((task) => task?.taskType === SOOCOOL_TASK_TYPE_DELIVERY);
  if (deliveryTasks.length === 0) return tasks[0] || null;

  let current = deliveryTasks[deliveryTasks.length - 1];
  let highestId = null;
  deliveryTasks.forEach((task) => {
    // Number(null) and Number("") are both 0, which would beat a real id - guard the raw value.
    if (task?.taskId === null || task?.taskId === undefined || task?.taskId === "") return;
    const taskId = Number(task.taskId);
    if (!Number.isFinite(taskId)) return;
    if (highestId === null || taskId > highestId) {
      highestId = taskId;
      current = task;
    }
  });
  return current;
};

/**
 * The track & trace link of the *current* delivery attempt; null while the order is still `processing`.
 *
 * SooCool issues a link per task, so after a redelivery the first task still carries the link of the
 * attempt that failed - showing that next to a "Bezorgd" status would send staff (and the customer)
 * to the wrong tracking page. Falls back to the first link of any delivery task, which is what every
 * single-delivery shipment resolves to anyway - but never to the pickup's: that page follows the boxes
 * to SooCool, not to the customer, and it has a link before the delivery does.
 */
export const getSooCoolTrackAndTraceLink = (soocoolOrder) => {
  const current = getSooCoolDeliveryTask(soocoolOrder);
  if (current?.trackAndTraceLink) return current.trackAndTraceLink;
  return (
    (soocoolOrder?.tasks || [])
      .filter((task) => task?.taskType === SOOCOOL_TASK_TYPE_DELIVERY)
      .map((task) => task?.trackAndTraceLink)
      .find((link) => !!link) || null
  );
};

/**
 * Our own track & trace mail for this delivery task: `{ taskId, mailedAt, email }`, or null when it
 * has not gone out (yet). The api sends one per delivery attempt once SooCool has planned it, and
 * keeps them in `soocool.trackAndTraceMails` (SooCoolTrackAndTraceMailService).
 */
export const getSooCoolTrackAndTraceMail = (soocoolOrder, task) => {
  if (task?.taskId === null || task?.taskId === undefined || task?.taskId === "") return null;
  const taskId = Number(task.taskId);
  return (soocoolOrder?.trackAndTraceMails || []).find((entry) => Number(entry?.taskId) === taskId) || null;
};

export const getSooCoolPlannedTimeWindow = (task) =>
  task?.plannedTimeWindow || task?.trackAndTraceInfo?.plannedTimeWindow || null;

export const getSooCoolCompletionProblem = (task) =>
  task?.completionProblem || task?.trackAndTraceInfo?.completionProblem || null;

/**
 * Did the courier leave the parcel with the neighbours? Our own write-back stores this flat on the
 * task, the raw SooCool object nests it under `trackAndTraceInfo`, so both are read here.
 */
export const getSooCoolDeliveredAtNeighbours = (task) =>
  !!(task?.deliveredAtNeighbours ?? task?.trackAndTraceInfo?.deliveredAtNeighbours);

/** The neighbour's house number, when SooCool passed one along. Null otherwise. */
export const getSooCoolNeighbourHouseNumber = (task) =>
  task?.neighbourHouseNumber || task?.trackAndTraceInfo?.neighbourHouseNumber || null;

export const getSooCoolTrackAndTraceInfo = (task) => task?.trackAndTraceInfo || null;

/** Colli weight in grams, from either shape. Returns null when SooCool has no weight for the box. */
export const getSooCoolGoodWeightGram = (good) => {
  const weight = good?.weightGram ?? good?.weight;
  if (weight === null || weight === undefined || weight === "") return null;
  const parsed = Number(weight);
  return Number.isFinite(parsed) ? parsed : null;
};

/** Number of boxes on a shipment. A SooCool order always has at least one `goods[]` entry. */
export const getSooCoolColliCount = (soocoolOrder) => (soocoolOrder?.goods || []).length;

/**
 * A colli weight the way staff read it off a box: kilos once it passes a kilo, grams below that.
 * Returns null when SooCool holds no weight for the box, so the caller can print its own dash.
 */
export const formatSooCoolGoodWeight = (good) => {
  const grams = getSooCoolGoodWeightGram(good);
  if (grams === null) return null;
  if (grams >= 1000) {
    return `${(grams / 1000).toLocaleString("nl-NL", { minimumFractionDigits: 0, maximumFractionDigits: 2 })} kg`;
  }
  return `${grams.toLocaleString("nl-NL")} g`;
};

const formatDateTime = (value) => {
  if (!value) return null;
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format(DATE_FORMAT_DD_MM_YYYY_HH_mm) : String(value);
};

/**
 * Render a `{startTime, endTime}` window as one line. Both ends nearly always fall on the same
 * day, so the date is printed once ("23 september 2026 17:00 - 22:00").
 */
export const formatSooCoolTimeWindow = (timeWindow) => {
  if (!timeWindow) return null;
  if (typeof timeWindow === "string") return timeWindow;

  const start = formatDateTime(timeWindow.startTime);
  const end = formatDateTime(timeWindow.endTime);
  if (!start && !end) return null;
  if (!start) return end;
  if (!end) return start;

  const startMoment = dayjs(timeWindow.startTime);
  const endMoment = dayjs(timeWindow.endTime);
  if (startMoment.isValid() && endMoment.isValid() && startMoment.isSame(endMoment, "day")) {
    return `${startMoment.format(DATE_FORMAT_DD_MM_YYYY_HH_mm)} - ${endMoment.format(DATE_FORMAT_HH_mm)}`;
  }
  return `${start} - ${end}`;
};

/** A SooCool address object as a single readable line. */
export const formatSooCoolAddress = (address) => {
  if (!address) return null;
  const street = [address.street, address.houseNumber].filter(Boolean).join(" ");
  const city = [address.postCode, address.city].filter(Boolean).join(" ");
  const line = [street, city, address.country && address.country !== "NL" ? address.country : null]
    .filter(Boolean)
    .join(", ");
  return line || null;
};

/** Organisation, or person, whichever SooCool holds for the address. */
export const formatSooCoolAddressee = (address) => address?.organization || address?.person || null;
